import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useLanguage, tr } from "../i18n";
import { lp } from "../components/Navbar";
import { PageHero, CTABand } from "./Shared";
import { Reveal, Stagger, StaggerItem, Kicker, SectionHead } from "../components/motion";
const SERVICES = [{
  id: "eri",
  kicker: "Service 01",
  title: "Ethical Readiness Index",
  price: "FREE",
  priceNote: "No payment required",
  condition: "For organizations wanting an initial AI readiness assessment.",
  includes: ["Ethical readiness assessment", "Initial AI governance overview", "Identification of key risk areas", "Readiness score", "Initial improvement areas"],
  cta: {
    label: "Start Free ERI",
    to: "/eri"
  },
  testid: "service-eri"
}, {
  id: "individual",
  kicker: "Service 02",
  title: "Single-Entity AI Assessment",
  price: "From €750",
  priceNote: "per entity",
  condition: "Designed for organizations with 1 to 10 legal entities.",
  includes: ["ESAIA AI assessment", "AI governance assessment", "AI responsibility assessment", "AI risk assessment", "AI systems and/or AI agent assessment", "Ethical and operational readiness assessment", "Risk heatmap", "Gap analysis", "Executive summary", "Remediation recommendations", "ESAIA Certification Report", "ESAIA Certificate"],
  validity: "12 months. Continued certification requires a new ESAIA reassessment after the certification period.",
  cta: {
    label: "Request a Quote",
    to: "/contact?service=Single-Entity%20AI%20Assessment"
  },
  testid: "service-individual"
}, {
  id: "group",
  kicker: "Service 03",
  title: "Group-Wide AI Assessment",
  price: "From €2,500 - €15,000",
  priceNote: "based on scope, size and complexity",
  condition: "Designed for organizations with more than 10 legal entities, or organizations that qualify as a large or complex company or group deploying multiple AI systems.",
  includes: ["Group-level ESAIA assessment", "Group-wide AI governance assessment", "AI inventory assessment", "AI risk management assessment", "Assessment of relevant AI systems and agents", "Entity-level risk considerations", "Governance structure assessment", "Risk heatmap", "Gap analysis", "Executive management report", "Remediation recommendations", "ESAIA Certification Report", "ESAIA Group Certificate"],
  validity: "12 months. A new assessment is required to renew certification.",
  cta: {
    label: "Request a Group Quote",
    to: "/contact?service=Group-Wide%20AI%20Assessment"
  },
  testid: "service-group"
}, {
  id: "recertification",
  kicker: "Service 04",
  title: "Annual ESAIA Recertification",
  price: "Custom quotation",
  priceNote: "based on changes since the previous assessment",
  condition: "For certified organizations seeking to maintain certification. ESAIA certification is valid for 12 months and requires successful annual reassessment for renewal.",
  includes: ["Review of changes since previous assessment", "New AI systems and new AI agents", "Material changes to existing AI systems", "Governance changes and risk changes", "Review of previous findings", "Remediation progress", "Updated risk assessment", "Updated ESAIA report", "Renewed certificate"],
  cta: {
    label: "Request Renewal Quote",
    to: "/contact?service=Annual%20Recertification"
  },
  testid: "service-recertification"
}];
const ADDITIONAL = ["New AI agent", "New AI system", "Materially changed AI system", "New business process involving AI", "New entity", "New deployment environment"];
export default function Services() {
  const {
    lang
  } = useLanguage();
  return <div data-testid="services-page">
      <PageHero kicker={tr("AI Assurance Services")} title={tr("Assurance for every stage of AI adoption.")} copy={tr("ESAIA provides independent AI assurance services designed for organizations at different stages of AI adoption, from initial readiness assessment to full organizational certification and annual reassessment.")} />

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <Stagger className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {SERVICES.map(s => <StaggerItem key={s.id}>
              <div className="flex h-full flex-col border border-esaia-line bg-white transition-all duration-300 hover:border-esaia-blue/50 hover:shadow-md" data-testid={s.testid}>
                <div className="border-b border-esaia-line p-8 sm:p-10">
                  <div className="flex items-start justify-between gap-4">
                    <Kicker>{tr(s.kicker)}</Kicker>
                    <span className="text-right">
                      <span className="block text-lg font-semibold text-esaia-ink">{tr(s.price)}</span>
                      <span className="block text-xs text-esaia-subtle">{tr(s.priceNote)}</span>
                    </span>
                  </div>
                  <h2 className="mt-4 text-xl font-semibold text-esaia-ink sm:text-2xl">{tr(s.title)}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-esaia-muted">{tr(s.condition)}</p>
                </div>
                <div className="flex-1 p-8 sm:p-10">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-subtle">{tr("What is included")}</p>
                  <ul className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    {s.includes.map(inc => <li key={inc} className="flex items-start gap-2 text-sm text-esaia-muted">
                        <Check size={15} className="mt-0.5 shrink-0 text-esaia-blue" />
                        {tr(inc)}
                      </li>)}
                  </ul>
                  {s.validity && <p className="mt-6 border-l-2 border-esaia-blue/40 pl-4 text-xs leading-relaxed text-esaia-subtle">{tr("Certification validity:")}{" "}{tr(s.validity)}
                    </p>}
                </div>
                <div className="border-t border-esaia-line p-8 pt-6">
                  <Link to={lp(lang, s.cta.to)} data-testid={`${s.testid}-cta`} className="group inline-flex items-center gap-2 bg-esaia-blue px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark">
                    {tr(s.cta.label)}
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </StaggerItem>)}
        </Stagger>

        <Reveal delay={0.1} className="mt-6">
          <p className="text-xs leading-relaxed text-esaia-subtle" data-testid="pricing-disclaimer">{tr("Final pricing depends on the size of the organization, organizational complexity, number and nature of AI systems or AI agents in scope, and overall assessment requirements. Group pricing additionally depends on group size, number of entities, geographic structure and AI environment complexity.")}{" "}</p>
        </Reveal>
      </section>

      <section className="border-y border-esaia-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHead kicker={tr("AI changes. Your assurance should too.")} title={tr("Additional AI System and Agent Assessments")} copy={tr("Organizations may request assessments outside the initial certification scope. Pricing is provided as a custom quotation and depends on complexity, risk, scope, number of systems and agents, data sensitivity and organizational impact.")} />
              <Reveal delay={0.2} className="mt-8">
                <Link to={lp(lang, "/contact?service=AI%20System%20Assessment")} data-testid="discuss-scope-button" className="group inline-flex items-center gap-2 bg-esaia-blue px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark">{tr("Discuss Your Scope")}{" "}<ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Stagger className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {ADDITIONAL.map(a => <StaggerItem key={a}>
                    <div className="flex h-full items-center gap-3 border border-esaia-line bg-esaia-canvas p-5">
                      <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-esaia-blue" />
                      <span className="text-sm font-medium text-esaia-ink">{tr(a)}</span>
                    </div>
                  </StaggerItem>)}
              </Stagger>
            </div>
          </div>
        </div>
      </section>

      <CTABand title={tr("Not sure which service fits your organization?")} copy={tr("Tell us about your AI environment and we will help you determine the appropriate assessment scope.")} primary={{
      to: "/contact",
      label: tr("Request a Consultation")
    }} secondary={{
      to: "/pricing",
      label: tr("Compare Pricing")
    }} />
    </div>;
}
